import {JSX} from 'solid-js';
import {Repeater} from 'Shared/forms/Repeater';
import {reconcile} from 'solid-js/store';
import { IStoreWrap, useStore } from 'Shared/StoreProvider';


export interface ISolidRepeaterProps<ItemData> {
	field: string,
	renderTitle: (current:ItemData) => string,
	renderAddLabel?: () => JSX.Element|string,
	deleteItem?: (event:Event,index:number) => void,
	hideDelete?: boolean,
	afterReorder?: () => void,
	onChange?: (index:number,item:ItemData) => void,
	current: () => number|undefined,
	setCurrent?: (index:number|undefined) => void,
	barStyle?: any,
	children: (item:ItemData[],index:number) => JSX.Element,
}

export function StoreRepeater<ItemData>(P:ISolidRepeaterProps<ItemData>)
{
	const sw = useStore();

	/*
		Convert the store and location into something more like a signal. Passing in a signal would be
		more convenient here, but the DB operations are expecting a store.
	*/
	/* Allowing the array to be undefined. */
	const items:ItemData[] = () => valueFromStore(sw,P.field) ?? [];
	const setItems = (value:ItemData) => sw.setStore(...sw.location,P.field,value);

	const deleteFunc = P.deleteItem ?? ((e,i) => confirmAndDeleteItem(e,sw,P.field,i));

	return (
		<Repeater ref={P.ref} children={P.children}
			renderTitle={P.renderTitle}
			items={items()} 
			setItems={setItems} 
			deleteItem={!P.hideDelete && ((e:Event,i:number) => deleteFunc(e,i))}
			reorder={async (oldIndex:number,newIndex:number) => {
				await sw.server.reorderArray.mutate({
					page:sw.page,permission:sw.permission,id:sw.docId,location:[...sw.location,P.field],oldIndex,newIndex});
				P.afterReorder?.();
			}}
			current={P.current}
			setCurrent={P.setCurrent}
			barStyle={P.barStyle}
		/>
	);
}

export async function addItem<D>(sw:IStoreWrap<D>,field:string,initData:any)
{
	/* Update the browser: */
	let value = valueFromStore(sw,field);

	if (value==undefined) {
		/* Create the list and add item */
		sw.setStore(...sw.location,field,[]);
//XXX odd		
		value = valueFromStore(sw,field);
		//TODO try optimistic?
       	await sw.server.patch.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:sw.location,fields:[]});
	}

	/* Add item to existing list */
	sw.setStore(...sw.location,field,value.length,reconcile(initData));

//XXX maybe make these updates optimistic?		
	await sw.server.createArrayItem.mutate({page:sw.page,permission:sw.permission,id:sw.docId,
		location:[...sw.location,field],fields:initData});
}

export async function addItemAndOpen<D>(sw:IStoreWrap<D>,field:string,itemsNode:HTMLElement,initData:any)
{
	await addItem(sw,field,initData);

//FIXME use ref instead	
	const kids = [...itemsNode.children];

//TODO use an 'open' callback instead
	kids[kids.length - 1].querySelector('repeater-bar').click();
}

export async function deleteItem<D>(sw:IStoreWrap<D>,field:string,index:number)
{
	const array = valueFromStore(sw,field);
	const newArray = [...array.slice(0,index), ...array.slice(index+1)]; 
	sw.setStore(...sw.location,field,newArray);

	await sw.server.deleteArrayItem.mutate({page:sw.page,permission:sw.permission,id:sw.docId,location:[...sw.location,field],index});
}

export async function confirmAndDeleteItem<D>(event:Event,sw:IStoreWrap<D>,field:string,index:number)
{
	event.stopPropagation();

	if (!confirm('Are you sure you wish to delete this item?'))
		return;

	await deleteItem(sw,field,index);
}

/* Returns undefined if not found */
function valueFromStore(sw:IStoreWrap<any>,field:string)
{
	const parentPath = sw.location ?? [];

	let pos = sw.store;
	for (const part of parentPath) 
		pos = pos?.[part];

	if (pos==undefined)
		return undefined;

	return pos[field];
}

